import { Box } from '@mui/material'
import {
  LOVE_ROOT_TAG_ID,
  RATING_SCALE,
  READINESS_ROOT_TAG_ID,
  type RatingDimension,
  ratingChildTagId,
} from '@shared/tags/canonicalRatingTags'
import type { TagDefinition } from '@shared/tags/tagRegistry'
import React, { useState } from 'react'

interface RatingDotsProps {
  dimension: RatingDimension
  value: number | undefined
  onChange?: (next: number | undefined) => void
  tagRegistry: Record<string, TagDefinition>
  readOnly?: boolean
  size?: 'small' | 'medium'
  scale?: number
  ariaLabel?: string
}

/**
 * Horizontal dot-meter for rating tags (love / readiness / ...).
 * - Click dot N → set value to N.
 * - Click the currently-set dot → clear (emit undefined).
 * - Dot colors resolve from the canonical rating-child tag defs in the registry.
 */
export function RatingDots({
  dimension,
  value,
  onChange,
  tagRegistry,
  readOnly = false,
  size = 'small',
  scale = RATING_SCALE,
  ariaLabel,
}: RatingDotsProps) {
  const [hover, setHover] = useState<number | null>(null)
  const px = size === 'small' ? 10 : 14
  const gap = size === 'small' ? 2 : 3

  const interactive = !readOnly && typeof onChange === 'function'
  const display = hover ?? value ?? 0

  const familyRootId = dimension === 'love' ? LOVE_ROOT_TAG_ID : READINESS_ROOT_TAG_ID
  const familyColor = tagRegistry[familyRootId]?.color ?? '#9e9e9e'
  const unsetBorder = `color-mix(in srgb, ${familyColor} 45%, transparent)`

  return (
    <Box
      role={interactive ? 'radiogroup' : undefined}
      aria-label={ariaLabel}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: `${gap}px` }}
      onMouseLeave={() => setHover(null)}
    >
      {Array.from({ length: scale }, (_, i) => {
        const n = i + 1
        const filled = n <= display
        const dotColor = tagRegistry[ratingChildTagId(dimension, n)]?.color
        const borderColor = filled ? (dotColor ?? familyColor) : unsetBorder

        return (
          <Box
            key={n}
            role={interactive ? 'radio' : undefined}
            aria-checked={value === n}
            aria-label={ariaLabel ? `${ariaLabel} ${n}` : undefined}
            onClick={
              interactive
                ? (ev) => {
                    ev.stopPropagation()
                    onChange?.(value === n ? undefined : n)
                  }
                : undefined
            }
            onMouseEnter={interactive ? () => setHover(n) : undefined}
            sx={{
              width: px,
              height: px,
              borderRadius: '50%',
              boxSizing: 'border-box',
              cursor: interactive ? 'pointer' : 'default',
              backgroundColor: filled ? (dotColor ?? 'var(--text-muted, #9e9e9e)') : 'transparent',
              border: `1px solid ${borderColor}`,
              transition: 'background-color 0.12s, transform 0.12s, border-color 0.12s',
              '&:hover': interactive ? { transform: 'scale(1.15)' } : undefined,
            }}
          />
        )
      })}
    </Box>
  )
}
